import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import copy from 'clipboard-copy';
import * as Sentry from '@sentry/browser';

import useToast from '../../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import FirebaseContext from '../../../../../../../context/FirebaseContext';
import LoadingBadge from '../../../../../../../components/LoadingBadge';
import { ModalBody, ModalActionButton } from '../../../../../../../components/DialogRoundedModal';
import { summarizeNotes, SummaryScope, MAX_TEXT_LENGTH } from './utils';
import texts from './texts.json';
import {
  SummaryModal,
  LoadingRow,
  BulletList,
  Message,
  Warning,
  Actions,
  ActionButtonsRow,
  SecondaryActionButton,
} from './styles';

const Status = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
  EMPTY: 'empty',
  NO_SUMMARY: 'no_summary',
  TOO_LONG: 'too_long',
};

const NotesSummaryPopover = ({
  scope, selectedText, onReplace, onClose,
}) => {
  const scopeTexts = texts.scope[scope];
  const { showToast } = useToast();
  const { firebase: { remote } } = useContext(FirebaseContext);
  const isComponentMountedRef = useComponentMounted();
  const [status, setStatus] = useState(Status.LOADING);
  const [bullets, setBullets] = useState([]);

  const run = useCallback(async () => {
    setStatus(Status.LOADING);
    if (!selectedText) {
      setStatus(Status.EMPTY);
      return;
    }
    if (selectedText.length > MAX_TEXT_LENGTH) {
      setStatus(Status.TOO_LONG);
      return;
    }
    try {
      const result = await summarizeNotes({ text: selectedText, remote });
      if (!isComponentMountedRef.current) return;
      if (!result.length) {
        setStatus(Status.NO_SUMMARY);
        return;
      }
      setBullets(result);
      setStatus(Status.SUCCESS);
    } catch (error) {
      Sentry.captureException(error);
      if (isComponentMountedRef.current) setStatus(Status.ERROR);
    }
  }, [selectedText, remote, isComponentMountedRef]);

  useEffect(() => {
    run();
  }, [run]);

  const handleCopy = useCallback(() => {
    copy(bullets.map((bullet) => `• ${bullet}`).join('\n'));
    showToast(texts.copiedSummary);
  }, [bullets, showToast]);

  const handleReplace = useCallback(async () => {
    if (await onReplace(bullets)) showToast(scopeTexts.replaced);
  }, [onReplace, bullets, showToast, scopeTexts]);

  const title = status === Status.LOADING || status === Status.SUCCESS
    ? scopeTexts.title
    : scopeTexts.actionTitle;

  let description = '';
  if (status === Status.SUCCESS) {
    description = bullets.length === 1
      ? texts.onePointBadge
      : format(texts.pointsBadge, { count: bullets.length });
  }

  const message = {
    [Status.ERROR]: texts.errorMessage,
    [Status.NO_SUMMARY]: scopeTexts.noSummary,
    [Status.EMPTY]: scopeTexts.empty,
    [Status.TOO_LONG]: scopeTexts.tooLong,
  }[status];

  let actionButtons = null;
  if (status === Status.SUCCESS) {
    actionButtons = (
      <Actions>
        <ActionButtonsRow>
          <ModalActionButton onClick={handleReplace}>
            {scopeTexts.replace}
          </ModalActionButton>
          <SecondaryActionButton onClick={handleCopy}>
            {texts.copy}
          </SecondaryActionButton>
        </ActionButtonsRow>
        <Warning>{texts.replaceWarning}</Warning>
      </Actions>
    );
  } else if (status === Status.ERROR || status === Status.NO_SUMMARY) {
    actionButtons = (
      <ModalActionButton onClick={run}>{texts.retry}</ModalActionButton>
    );
  }

  return (
    <SummaryModal
      open
      onClose={onClose}
      title={title}
      description={description}
      maxWidth="xs"
      fullWidth
      actionButtons={actionButtons}
    >
      <ModalBody>
        {status === Status.LOADING && (
          <LoadingRow>
            <LoadingBadge />
            {scopeTexts.loading}
          </LoadingRow>
        )}

        {status === Status.SUCCESS && (
          <BulletList>
            {bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </BulletList>
        )}

        {!!message && <Message>{message}</Message>}
      </ModalBody>
    </SummaryModal>
  );
};

NotesSummaryPopover.propTypes = {
  scope: PropTypes.oneOf(Object.values(SummaryScope)).isRequired,
  selectedText: PropTypes.string.isRequired,
  onReplace: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NotesSummaryPopover;
